import React, { useEffect, useState, useMemo } from 'react';
import styles from './SalesDashboard.module.css';
import { paymentAPI } from '../../services/paymentAPI';

const SalesDashboard = () => {
  const [stats, setStats] = useState({ total_sales: 0, new_customers: 0, low_stock: 0 });
  const [transactions, setTransactions] = useState([]); // Agora guarda pagos e despachados
  const [filterRange, setFilterRange] = useState('monthly');
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // Novo filtro de status
  const [loading, setLoading] = useState(true);

  // Dicionário para exibição na tabela
  const statusTranslate = {
    paid: { label: 'Pago', color: '#27ae60' },
    shipped: { label: 'Despachado', color: '#2980b9' }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await paymentAPI.order_admin();
        const allOrders = res?.data || res || [];
        
        // Filtra apenas o que interessa para o financeiro: pago ou despachado
        const relevantOrders = Array.isArray(allOrders) 
          ? allOrders.filter(order => order.status === 'paid' || order.status === 'shipped') 
          : [];

        const totalRevenue = relevantOrders.reduce((acc, curr) => acc + parseFloat(curr.total || 0), 0);

        setStats({
          total_sales: totalRevenue,
          new_customers: new Set(relevantOrders.map(o => o.customer?.email || o.cpf)).size, 
          low_stock: 5 
        });
        
        setTransactions(relevantOrders);
      } catch (err) {
        console.error("Erro ao carregar dados:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredData = useMemo(() => {
    const now = new Date();
    let startDate = new Date();
    if (filterRange === 'daily') startDate.setDate(now.getDate() - 1);
    else if (filterRange === 'monthly') startDate.setMonth(now.getMonth() - 1);
    else startDate.setFullYear(now.getFullYear() - 1);

    return transactions.filter(t => {
      const d = new Date(t.date);
      const matchesDate = d >= startDate;
      const matchesProduct = productFilter ? t.items?.some(i => i.product_name === productFilter) : true;
      const matchesStatus = statusFilter === 'all' ? true : t.status === statusFilter;
      
      return matchesDate && matchesProduct && matchesStatus;
    });
  }, [transactions, filterRange, productFilter, statusFilter]);

  const chartPoints = useMemo(() => {
    if (filteredData.length < 2) return "";
    const series = filteredData.map(o => parseFloat(o.total || 0));
    const max = Math.max(...series, 1);
    return series.map((v, i) => 
      `${(i / (series.length - 1)) * 600},${120 - (v / max) * 100}`
    ).join(' ');
  }, [filteredData]);

  if (loading) return <div className={styles.loader}>Carregando dashboard...</div>;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Visão Geral de Vendas</h1>
        <div className={styles.filters}>
          {/* Filtro de Período */}
          <select value={filterRange} onChange={e => setFilterRange(e.target.value)} className={styles.select}>
            <option value="daily">Últimas 24h</option>
            <option value="monthly">Últimos 30 dias</option>
            <option value="annual">Último ano</option>
          </select>

          {/* NOVO: Filtro de Status */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={styles.select}>
            <option value="all">Todos os Status</option>
            <option value="paid">Pagos</option>
            <option value="shipped">Despachados</option>
          </select>
          
          {/* Filtro de Produto */}
          <select value={productFilter} onChange={e => setProductFilter(e.target.value)} className={styles.select}>
            <option value="">Todos os produtos</option>
            {[...new Set(transactions.flatMap(t => t.items?.map(i => i.product_name) || []))].map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
      </header>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Receita Total (Aprovada/Despachada)</span>
          <h2 className={styles.statValue}>
            {stats.total_sales.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </h2>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Clientes Únicos</span>
          <h2 className={styles.statValue}>{stats.new_customers}</h2>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Alertas de Estoque</span>
          <h2 className={`${styles.statValue} ${stats.low_stock > 0 ? styles.danger : ''}`}>
            {stats.low_stock}
          </h2>
        </div>
      </div>

      <div className={styles.mainGrid}>
        <section className={styles.chartSection}>
          <div className={styles.sectionHeader}>
            <h3>Desempenho de Vendas ({statusFilter === 'all' ? 'Geral' : statusTranslate[statusFilter].label})</h3>
          </div>
          <div className={styles.chartWrapper}>
            {filteredData.length > 0 ? (
              <svg viewBox="0 0 600 120" className={styles.svgChart}>
                <polyline fill="none" stroke="#6366f1" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" points={chartPoints} />
              </svg>
            ) : (
              <p className={styles.emptyMsg}>Nenhum dado encontrado para os filtros selecionados.</p>
            )}
          </div>
        </section>

        <section className={styles.tableSection}>
          <h3>Transações Recentes</h3>
          <div className={styles.tableResponsive}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, 8).map(tx => (
                  <tr key={tx.order_id}>
                    <td>#{tx.order_id}</td>
                    <td>
                      <div className={styles.customerCell}>
                        <span className={styles.customerName}>{tx.customer?.name || 'Consumidor'}</span>
                        <span className={styles.customerEmail}>{tx.customer?.email || 'CPF: ' + tx.cpf}</span>
                      </div>
                    </td>
                    <td className={styles.priceCell}>R$ {parseFloat(tx.total).toFixed(2)}</td>
                    <td>
                      <span 
                        className={styles.statusBadge} 
                        style={{ 
                            backgroundColor: statusTranslate[tx.status]?.color + '22', // 22 é a transparência em Hex
                            color: statusTranslate[tx.status]?.color,
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}
                      >
                        {statusTranslate[tx.status]?.label || tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SalesDashboard;